import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAdminUsers } from '../../lib/api';
import type { AdminUserListItem } from '../../lib/types';
import { AdminBadge, AdminEmptyState, AdminPanel, AdminStatCard, formatDateTime } from './AdminUi';

export default function AdminUsersPage() {
  const [query, setQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [items, setItems] = useState<AdminUserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await getAdminUsers(activeQuery || undefined);
        if (!disposed) {
          setItems(response.users);
          setErrorMessage(null);
        }
      } catch (error) {
        if (!disposed) {
          const apiError = error as Error;
          setErrorMessage(apiError.message || '관리자 사용자 목록을 불러오지 못했습니다.');
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      disposed = true;
    };
  }, [activeQuery]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActiveQuery(query.trim());
  };

  return (
    <div className="space-y-6">
      <AdminPanel title="사용자 지원" subtitle="이메일 기준으로 사용자를 빠르게 찾고 최근 리딩 상태를 확인합니다">
        <form className="flex flex-wrap gap-3" onSubmit={handleSubmit}>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="이메일 또는 이름으로 검색"
            className="min-w-[260px] flex-1 border border-[#8a6a4b]/30 bg-[#120d0b] px-3 py-3 text-sm text-[#f4e2cb] outline-none focus:border-[#fbbf24]/60"
          />
          <button
            type="submit"
            className="border border-[#8a6a4b]/45 bg-[#241913] px-4 py-3 text-sm font-medium text-[#f3dfc5] hover:bg-[#2d2019]"
          >
            검색
          </button>
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setActiveQuery('');
            }}
            className="border border-[#8a6a4b]/25 bg-[#17110f] px-4 py-3 text-sm text-[#d8bfa1]/80 hover:bg-[#1f1511]"
          >
            초기화
          </button>
        </form>
      </AdminPanel>

      <AdminPanel title="사용자 목록" subtitle="최근 리딩 활동과 지원용 지표를 함께 보여줍니다">
        {loading ? (
          <p className="text-sm text-[#d8bfa1]/75">사용자 목록을 불러오고 있습니다...</p>
        ) : errorMessage ? (
          <AdminEmptyState title="사용자 목록을 불러올 수 없습니다" body={errorMessage} />
        ) : items.length === 0 ? (
          <AdminEmptyState title="조건에 맞는 사용자가 없습니다" body="다른 이메일 조각으로 검색하거나 새 로그인 사용자를 기다려 주세요." />
        ) : (
          <div className="space-y-4">
            {items.map((user) => (
              <article key={user.id} className="border border-[#8a6a4b]/30 bg-[#140f0d]/90 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-lg font-semibold text-[#f4e2cb]">{user.name || user.email}</h3>
                      <p className="mt-1 text-sm text-[#d8bfa1]/72">{user.email}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <AdminBadge label={user.provider} tone="muted" />
                      <AdminBadge label={`활성 세션 ${user.active_session_count}개`} tone="accent" />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <AdminStatCard label="리딩 수" value={user.reading_count} />
                      <AdminStatCard label="접근 가능" value={user.active_reading_count} />
                      <AdminStatCard label="최근 리딩" value={formatDateTime(user.latest_reading_at)} />
                    </div>
                  </div>
                  <Link
                    to={`/admin/users/${user.id}`}
                    className="inline-flex items-center gap-2 border border-[#8a6a4b]/45 bg-[#241913] px-3 py-2 text-sm text-[#f3dfc5] hover:bg-[#2d2019]"
                  >
                    사용자 열기
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </AdminPanel>
    </div>
  );
}
